/**
 * Detection du format d'ecran.
 *
 * Source unique de verite pour les seuils : le JavaScript et la feuille de
 * style se contredisaient auparavant, le premier choisissant la structure
 * pendant que la seconde appliquait ses propres regles au meme moment.
 *
 * Ici le JavaScript ne decide que de la structure (empile ou cote a cote) ; la
 * feuille de style ne gere plus que l'apparence.
 */

import { useEffect, useState } from 'react';

export const BREAKPOINTS = {
  /** En dessous : editeur et apercu empiles. */
  desktop: 1024,
  /** Au-dessus : l'apercu peut occuper une part plus large. */
  wide: 1440,
} as const;

export type Breakpoint = 'compact' | 'desktop' | 'wide';

const resolve = (width: number): Breakpoint => {
  if (width < BREAKPOINTS.desktop) return 'compact';
  if (width < BREAKPOINTS.wide) return 'desktop';
  return 'wide';
};

export const useBreakpoint = (): Breakpoint => {
  const [breakpoint, setBreakpoint] = useState<Breakpoint>(() =>
    typeof window === 'undefined' ? 'desktop' : resolve(window.innerWidth)
  );

  useEffect(() => {
    let frame = 0;

    const update = () => {
      // Le redimensionnement emet en continu : on n'evalue qu'une fois par
      // trame, et l'etat n'est mis a jour que si le palier change vraiment.
      // L'ancienne version declenchait un rendu de tout l'arbre editeur a
      // chaque pixel.
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const next = resolve(window.innerWidth);
        setBreakpoint(current => (current === next ? current : next));
      });
    };

    window.addEventListener('resize', update);
    // La rotation d'un appareil ne declenche pas toujours `resize`.
    window.addEventListener('orientationchange', update);
    update();

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  return breakpoint;
};
